/**
 * Shopify Forms → lead_submitted (Customer Events → Meta Custom Pixel)
 *
 * Popup lives in an open shadow root. Success looks like:
 *   #app-embed[data-current-step="success"]
 *   [data-testid="banner-success"]
 */
(() => {
  const EVENT_NAME = 'lead_submitted';
  const SESSION_PREFIX = 'rt_forms_lead:';
  const DEBUG = /(?:\?|&)rt_forms_lead_debug=1(?:&|$)/.test(location.search);

  const boundRoots = new WeakSet();
  const observedEmbeds = new WeakSet();
  const fieldCache = new WeakMap();
  const firedHosts = new WeakSet();

  function log(...args) {
    if (DEBUG) console.info('[rt-forms-lead]', ...args);
  }

  function publishLead(payload) {
    const tryPublish = () => {
      if (!window.Shopify?.analytics?.publish) return false;
      try {
        Shopify.analytics.publish(EVENT_NAME, payload);
        log('published', payload);
        return true;
      } catch (err) {
        log('publish error', err);
        return false;
      }
    };

    if (tryPublish()) return;

    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (tryPublish() || attempts >= 40) clearInterval(timer);
    }, 250);
  }

  function normalizeEmail(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  function readFields(root) {
    if (!root) return {};

    const emailEl = root.querySelector(
      '#email, input[type="email"], input[name*="email"], input[autocomplete="email"]'
    );
    const firstEl = root.querySelector(
      '#first_name, #firstName, input[name*="first"], input[autocomplete="given-name"]'
    );
    const lastEl = root.querySelector(
      '#last_name, #lastName, input[name*="last"], input[autocomplete="family-name"]'
    );
    const phoneEl = root.querySelector(
      '#phone, input[type="tel"], input[name*="phone"], input[autocomplete="tel"]'
    );

    return {
      em: normalizeEmail(emailEl && emailEl.value),
      fn: String((firstEl && firstEl.value) || '').trim(),
      ln: String((lastEl && lastEl.value) || '').trim(),
      ph: String((phoneEl && phoneEl.value) || '').trim(),
    };
  }

  function isSuccess(root, appEmbed) {
    const step = appEmbed && appEmbed.getAttribute('data-current-step');
    if (step && String(step).toLowerCase() === 'success') return { ok: true, step: 'success' };
    if (step && /success|thank|complete|confirm/i.test(step)) return { ok: true, step };

    if (root && root.querySelector('[data-testid="banner-success"]')) {
      return { ok: true, step: step || 'banner-success' };
    }

    return { ok: false, step: step || '' };
  }

  function sessionKey(formId, email) {
    return SESSION_PREFIX + (formId || 'unknown') + ':' + (email || 'no-email');
  }

  function alreadyFiredSession(formId, email) {
    try {
      return sessionStorage.getItem(sessionKey(formId, email)) === '1';
    } catch (e) {
      return false;
    }
  }

  function markFiredSession(formId, email) {
    try {
      sessionStorage.setItem(sessionKey(formId, email), '1');
    } catch (e) {
      // ignore
    }
  }

  function cacheFields(host, root) {
    const fields = readFields(root);
    const prev = fieldCache.get(host) || {};
    const next = { ...prev };
    Object.keys(fields).forEach((key) => {
      if (fields[key]) next[key] = fields[key];
    });
    fieldCache.set(host, next);
    return next;
  }

  function fireLead(host, root, step) {
    if (firedHosts.has(host)) return;

    const formId =
      (host.getAttribute && host.getAttribute('data-forms-id')) ||
      host.id ||
      (root.querySelector('[data-forms-id]') &&
        root.querySelector('[data-forms-id]').getAttribute('data-forms-id')) ||
      '';

    const cached = cacheFields(host, root);
    const em = cached.em || '';
    const fn = cached.fn || '';
    const ln = cached.ln || '';
    const ph = cached.ph || '';

    if (alreadyFiredSession(formId, em)) {
      firedHosts.add(host);
      log('skip session duplicate', formId, em);
      return;
    }

    const payload = {
      content_name: 'Shopify Forms',
      content_category: 'Shopify Forms',
      lead_source: 'shopify_forms_shadow',
      form_id: formId,
      form_step: step || 'success',
    };

    if (em) payload.em = em;
    if (fn) payload.fn = fn;
    if (ln) payload.ln = ln;
    if (ph) payload.ph = ph;

    firedHosts.add(host);
    markFiredSession(formId, em);
    publishLead(payload);
  }

  function bindAppEmbed(host, root, appEmbed) {
    if (!appEmbed || observedEmbeds.has(appEmbed)) return;
    observedEmbeds.add(appEmbed);

    const check = () => {
      const result = isSuccess(root, appEmbed);
      log('check step', appEmbed.getAttribute('data-current-step'), result);
      if (result.ok) fireLead(host, root, result.step);
    };

    check();

    new MutationObserver(check).observe(appEmbed, {
      attributes: true,
      attributeFilter: ['data-current-step', 'class'],
      childList: true,
      subtree: true,
    });
  }

  function bindShadowHost(host) {
    const root = host.shadowRoot;
    if (!root || boundRoots.has(root)) return;
    boundRoots.add(root);

    log('bound host', host.tagName, host.id || host.className);

    const onFieldEvent = () => cacheFields(host, root);
    root.addEventListener('input', onFieldEvent, true);
    root.addEventListener('change', onFieldEvent, true);
    root.addEventListener('blur', onFieldEvent, true);
    root.addEventListener('submit', onFieldEvent, true);

    const attach = () => {
      const appEmbed =
        root.querySelector('#app-embed[data-form-root]') ||
        root.querySelector('#app-embed') ||
        root.querySelector('[data-form-root="true"]');

      if (appEmbed) bindAppEmbed(host, root, appEmbed);

      // Success banner can appear even if we missed the attribute mutation
      if (root.querySelector('[data-testid="banner-success"]')) {
        fireLead(host, root, 'banner-success');
      }
    };

    attach();
    new MutationObserver(attach).observe(root, { childList: true, subtree: true });
  }

  function collectShadowHosts(node, out) {
    if (!node || !node.querySelectorAll) return;

    node.querySelectorAll('*').forEach((el) => {
      if (el.shadowRoot) {
        out.add(el);
        collectShadowHosts(el.shadowRoot, out);
      }
    });
  }

  function scan() {
    const hosts = new Set();

    document.querySelectorAll('form-embed, [id^="app-embed-container"], [data-forms-id]').forEach((el) => {
      if (el.shadowRoot) hosts.add(el);
    });

    collectShadowHosts(document, hosts);
    hosts.forEach(bindShadowHost);
  }

  function start() {
    scan();
    new MutationObserver(scan).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
    // Forms often mounts after first paint / teaser click
    setTimeout(scan, 1000);
    setTimeout(scan, 3000);
    log('started');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
